import { z } from "zod";
import type { SupersededProposalReceiptData } from "@/lib/actionRequests/proposalReceipt";
import { EXPERIMENT_ENTITY_PREFIX, isValidPrefixedKsuid } from "@/utils/ksuidGenerator";

const INSIGHT_ENTITY_PREFIX = "ins_";
export const LEARNING_GATE_PROMPT =
  "Is the evidence strong enough to capture a learning?";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

export const LearningGatePayloadSchema = z
  .object({
    experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
    approvedInsightIds: z
      .array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"))
      .min(1)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: "approvedInsightIds must not contain duplicates",
      }),
    summary: z.string().min(1).max(4000),
    prompt: z.literal(LEARNING_GATE_PROMPT),
  })
  .strict();

/** Historical gate cards allowed arbitrary prompt copy, duplicate ids, and uncapped summaries. */
const LegacyLearningGatePayloadSchema = z.object({
  experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
  approvedInsightIds: z.array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight")).min(1),
  summary: z.string().min(1),
  prompt: z.string().min(1),
});

export const LearningGateResolveSchema = z
  .object({
    sufficientEvidence: z.boolean(),
    insufficientEvidenceRationale: z.string().trim().min(1).max(4000).optional(),
  })
  .strict()
  .refine(
    (data) => data.sufficientEvidence || Boolean(data.insufficientEvidenceRationale?.trim()),
    {
      message: "insufficientEvidenceRationale is required when evidence is insufficient",
      path: ["insufficientEvidenceRationale"],
    },
  );

export type LearningGatePayload = z.infer<typeof LearningGatePayloadSchema>;
export type LearningGateResolve = z.infer<typeof LearningGateResolveSchema>;

export type LearningGateToolResult =
  | (LearningGateResolve & { toolCallId: string })
  | SupersededProposalReceiptData;

type LearningGateEntity = {
  type?: string;
  experimentPublicId?: string;
  approvedInsightIds?: string[];
  summary?: string;
  prompt?: string;
  toolInvoke?: { toolCallId?: string };
};

export type LearningGateArgs = {
  entities?: LearningGateEntity[];
  experimentPublicId?: string;
  approvedInsightIds?: string[];
  summary?: string;
  prompt?: string;
};

/**
 * Parses learning_gate card payload from assistant tool args (flat or entities[] shape).
 */
export function parseLearningGatePayload(
  args: unknown,
  toolCallId?: string,
  allowHistoricalReceipt = false,
) {
  const direct = LearningGatePayloadSchema.safeParse(args);
  if (direct.success) {
    return direct;
  }
  if (allowHistoricalReceipt) {
    const historicalDirect = LegacyLearningGatePayloadSchema.safeParse(args);
    if (historicalDirect.success) {
      return historicalDirect;
    }
  }

  const root = args as LearningGateArgs | null | undefined;
  const entities = root?.entities ?? [];
  const entity =
    entities.find(
      (item) =>
        item.type === "learning_gate" &&
        (!toolCallId || item.toolInvoke?.toolCallId === toolCallId),
    ) ?? entities.find((item) => item.type === "learning_gate");

  if (!entity) {
    return direct;
  }

  const candidate = {
    experimentPublicId: entity.experimentPublicId ?? root?.experimentPublicId,
    approvedInsightIds: entity.approvedInsightIds ?? root?.approvedInsightIds,
    summary: entity.summary ?? root?.summary,
    prompt: entity.prompt ?? root?.prompt,
  };
  const currentEntity = LearningGatePayloadSchema.safeParse(candidate);
  if (currentEntity.success || !allowHistoricalReceipt) {
    return currentEntity;
  }
  return LegacyLearningGatePayloadSchema.safeParse(candidate);
}
